import { Link } from 'react-router-dom';
import { Facebook, Linkedin, Mail, MapPin, Phone, Smartphone } from 'lucide-react';
import { FooterProps } from '../../types';
import { CONTACT_INFO } from '../../constants';
import { useAppContext } from '../../context/AppContext';
import { getStaticPagePath, type StaticPageRouteKey, type UiLang } from '../../utils/routes';

export const Footer = ({ t, onOpenCookiePreferences }: FooterProps) => {
    const { activeLang } = useAppContext();
    const navText = t!.nav;
    const footerText = t!.footer;
    const contactText = t!.contactPage;
    const lang = activeLang.code.toLowerCase() as UiLang;
    const route = (key: StaticPageRouteKey) => getStaticPagePath(key, lang);

    const quickLinks = [
        { to: route('home'), label: navText.home },
        { to: route('services'), label: navText.services },
        { to: route('languages'), label: navText.languages },
        { to: route('about'), label: footerText.aboutUs },
        { to: route('news'), label: navText.news },
        { to: route('careers'), label: navText.careers },
        { to: route('faq'), label: footerText.faq },
        { to: route('contact'), label: navText.contact },
    ];

    const quickLinksFirstColumn = quickLinks.slice(0, 4);
    const quickLinksSecondColumn = quickLinks.slice(4, 8);

    return (
        <footer id="contact" className="bg-[#1a1a1a] text-stone-400 pt-12 pb-6 border-t border-stone-800">
            <div className="container mx-auto px-4">
                <div className="grid md:grid-cols-[1.1fr_0.9fr_1.35fr] gap-8 mb-8 items-start">
                    <div className="col-span-1 md:col-span-1">
                        <div className="flex items-center space-x-2 mb-6">
                            <span className="text-white font-serif text-2xl font-bold">Littera Magna</span>
                        </div>
                        <p className="text-sm leading-relaxed">{footerText.text}</p>
                    </div>

                    <div>
                        <h3 className="text-white font-bold mb-6 tracking-[0.08em] uppercase text-sm">{footerText.headers.nav}</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <ul className="space-y-2.5">
                                {quickLinksFirstColumn.map((item) => (
                                    <li key={item.to}>
                                        <Link to={item.to} className="hover:text-green-500 transition-colors">
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                            <ul className="space-y-2.5 sm:pl-3 sm:border-l sm:border-stone-800">
                                {quickLinksSecondColumn.map((item) => (
                                    <li key={item.to}>
                                        <Link to={item.to} className="hover:text-green-500 transition-colors">
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-6 lg:gap-5 items-start">
                        <div>
                            <h3 className="text-white font-bold mb-4 tracking-[0.08em] uppercase text-sm">{contactText.supportTitle}</h3>
                            <ul className="space-y-3 text-sm">
                                <li className="flex items-center space-x-3">
                                    <Mail size={18} className="text-green-600" />
                                    <span>{CONTACT_INFO.email}</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <Phone size={18} className="text-green-600" />
                                    <span>{CONTACT_INFO.phone}</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <Smartphone size={18} className="text-green-600" />
                                    <span>{CONTACT_INFO.phone2}</span>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-white font-bold mb-4 tracking-[0.08em] uppercase text-sm">{contactText.officeTitle}</h3>
                            <ul className="space-y-3 text-sm">
                                <li className="flex items-start space-x-3">
                                    <MapPin size={18} className="text-green-600 mt-0.5" />
                                    <span>{CONTACT_INFO.address}</span>
                                </li>
                                <li className="pl-8 text-stone-300">
                                    <div>{contactText.officeWeekdays}</div>
                                    <div className="mt-1">{contactText.officeWeekends}</div>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-white font-bold mb-4 tracking-[0.08em] uppercase text-sm whitespace-nowrap">{footerText.headers.followUs}</h3>
                            <div className="flex items-center space-x-3">
                                {CONTACT_INFO.facebook && (
                                    <a href={CONTACT_INFO.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center text-stone-400 hover:bg-green-700 hover:text-white transition-colors" aria-label="Facebook">
                                        <Facebook size={20} />
                                    </a>
                                )}
                                {CONTACT_INFO.linkedin && (
                                    <a href={CONTACT_INFO.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center text-stone-400 hover:bg-green-700 hover:text-white transition-colors" aria-label="LinkedIn">
                                        <Linkedin size={20} />
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="border-t border-stone-800 pt-8 flex flex-col md:flex-row justify-between items-center text-xs">
                    <p>&copy; {new Date().getFullYear()} UAB "Littera Magna". {footerText.rights}</p>
                    <div className="flex items-center space-x-4 mt-4 md:mt-0">
                        <Link to={route('privacy')} className="hover:text-green-500 transition-colors">{footerText.privacy}</Link>
                        <div className="w-1 h-1 bg-stone-600 rounded-full"></div>
                        <Link to={route('terms')} className="hover:text-green-500 transition-colors">{footerText.terms}</Link>
                        <div className="w-1 h-1 bg-stone-600 rounded-full"></div>
                        <button onClick={onOpenCookiePreferences} className="hover:text-green-500 transition-colors">{footerText.cookiePreferences}</button>
                    </div>
                </div>
            </div>
        </footer>
    );
};
